import { supabase } from './supabase';
import type { Database } from './types';
import { withCache } from './cache';

type Tables = Database['public']['Tables'];

// Helper function to chunk array into smaller arrays
const chunk = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

export const eventApi = {
  async getAll() {
    const { data, error } = await supabase
      .from('events')
      .select(`
        *,
        creator:profiles!events_created_by_fkey (
          id,
          name,
          email
        ),
        collaborator:profiles!events_collaborator_id_fkey (
          id,
          name,
          email
        )
      `)
      .order('date', { ascending: true });

    if (error) throw error;
    return data;
  },

  async create(event: Tables['events']['Insert']) {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('User not authenticated');

    const eventWithCreator = {
      ...event,
      created_by: user.id
    };

    const { data, error } = await supabase
      .from('events')
      .insert([eventWithCreator])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, event: Tables['events']['Update']) {
    const { data, error } = await supabase
      .from('events')
      .update(event)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('events')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

export const transactionApi = {
  async getAll(filters?: { startDate?: string; endDate?: string; type?: string; eventId?: string; category?: string }) {
    let query = supabase
      .from('transactions')
      .select(`
        *,
        event:events (
          id,
          name,
          date
        ),
        travel:travel_packages (
          id,
          title,
          start_date,
          end_date
        ),
        creator:profiles!transactions_created_by_fkey (
          id,
          name,
          email
        )
      `)
      .order('transaction_date', { ascending: false });

    if (filters?.startDate) {
      query = query.gte('transaction_date', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('transaction_date', filters.endDate);
    }
    if (filters?.type) {
      query = query.eq('type', filters.type);
    }
    if (filters?.eventId) {
      query = query.eq('event_id', filters.eventId);
    }
    if (filters?.category) {
      query = query.eq('business_area', filters.category);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async getSummary(filters?: { startDate?: string; endDate?: string; category?: string }) {
    let query = supabase
      .from('transactions')
      .select('type, amount');

    if (filters?.startDate) {
      query = query.gte('transaction_date', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('transaction_date', filters.endDate);
    }
    if (filters?.category) {
      query = query.eq('business_area', filters.category);
    }

    const { data, error } = await query;
    if (error) throw error;

    const income = data
      ?.filter(t => t.type === 'income')
      .reduce((sum, t) => sum + Number(t.amount), 0) || 0;

    const expenses = data
      ?.filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Number(t.amount), 0) || 0;

    return {
      income,
      expenses,
      balance: income - expenses
    };
  },

  async create(transaction: Tables['transactions']['Insert']) {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('User not authenticated');

    const transactionWithCreator = {
      ...transaction,
      created_by: user.id
    };

    const { data, error } = await supabase
      .from('transactions')
      .insert([transactionWithCreator])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, transaction: Tables['transactions']['Update']) {
    const { data, error } = await supabase
      .from('transactions')
      .update(transaction)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

export const agenziaAmoreApi = {
  async getAll() {
    const { data, error } = await supabase
      .from('agenzia_amore_clients')
      .select(`
        *,
        contact:contacts (
          id,
          name,
          email,
          phone,
          gender,
          birthdate,
          city
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async create(client: { contact_id: string; status: string; preferences?: string; notes?: string }) {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('User not authenticated');

    const clientWithCreator = {
      ...client,
      created_by: user.id
    };

    const { data, error } = await supabase
      .from('agenzia_amore_clients')
      .insert([clientWithCreator])
      .select(`
        *,
        contact:contacts (
          id,
          name,
          email,
          phone,
          gender,
          birthdate,
          city
        )
      `)
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, client: { status?: string; preferences?: string; notes?: string }) {
    const { data, error } = await supabase
      .from('agenzia_amore_clients')
      .update(client)
      .eq('id', id)
      .select(`
        *,
        contact:contacts (
          id,
          name,
          email,
          phone,
          gender,
          birthdate,
          city
        )
      `)
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('agenzia_amore_clients')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

export const contactApi = {
  async getAll() {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('User not authenticated');

    // Fetch user profile from database to get role and city
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('role, city, region')
      .eq('id', user.id)
      .single();

    if (profileError) throw profileError;

    const role = profile?.role;
    const userCity = profile?.city;
    const userRegion = profile?.region;

    let query = supabase
      .from('contacts')
      .select('*')
      .order('created_at', { ascending: false });

    // Only apply filters for non-admin users
    // Note: Collaborators' access is controlled by RLS policies based on their assigned events
    if (role === 'pr') {
      query = query.eq('created_by', user.id);
    } else if (role === 'area_leader' && userRegion) {
      const { getProvincesForRegion } = await import('../constants/regions');
      const regionProvinces = getProvincesForRegion(userRegion);
      if (regionProvinces.length > 0) {
        query = query.in('city', regionProvinces);
      }
    } else if (role === 'organizer') {
      query = query.eq('city', userCity);
    }

    const { data, error, count } = await query.select('*', { count: 'exact' });

    if (error) throw error;

    console.log(`User role: ${role}, city: ${userCity}`);
    console.log(`Total contacts in database: ${count}`);
    console.log(`Fetched contacts: ${data?.length}`);

    return data || [];
  },

  async create(contact: Tables['contacts']['Insert']) {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('User not authenticated');

    const contactWithCreator = {
      ...contact,
      created_by: user.id
    };

    const { data, error } = await supabase
      .from('contacts')
      .insert([contactWithCreator])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, contact: Tables['contacts']['Update']) {
    const { data, error } = await supabase
      .from('contacts')
      .update(contact)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateBatch(ids: string[], updates: Tables['contacts']['Update']) {
    const chunks = chunk(ids, 100);
    const errors: Error[] = [];

    for (const chunkIds of chunks) {
      try {
        const { error } = await supabase
          .from('contacts')
          .update(updates)
          .in('id', chunkIds);

        if (error) {
          errors.push(new Error(`Failed to update chunk: ${error.message}`));
        }
      } catch (error: any) {
        errors.push(new Error(`Failed to process chunk: ${error.message}`));
      }
    }

    if (errors.length > 0) {
      throw new Error(`Batch update errors: ${errors.map(e => e.message).join(', ')}`);
    }
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('contacts')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

export const eventParticipantsApi = {
  async getByEventId(eventId: string) {
    const { data, error } = await supabase
      .from('event_participants')
      .select(`
        *,
        participant:contacts (
          id,
          name,
          gender,
          birthdate
        )
      `)
      .eq('event_id', eventId)
      .order('created_at');
    
    if (error) throw error;
    return data;
  },

  async add(eventId: string, participantId: string) {
    const { error } = await supabase
      .from('event_participants')
      .insert({
        event_id: eventId,
        participant_id: participantId
      });
    
    if (error) throw error;
  },

  async remove(eventId: string, participantId: string) {
    const { error } = await supabase
      .from('event_participants')
      .delete()
      .eq('event_id', eventId)
      .eq('participant_id', participantId);
    
    if (error) throw error;
  }
};

export const matchApi = {
  async getAll() {
    const { data, error } = await supabase
      .from('matches')
      .select(`
        *,
        participant1:contacts!matches_participant1_id_fkey (
          id,
          name,
          gender,
          birthdate
        ),
        participant2:contacts!matches_participant2_id_fkey (
          id,
          name,
          gender,
          birthdate
        )
      `)
      .order('created_at');
    
    if (error) throw error;
    return data;
  },

  async getByEventId(eventId: string) {
    const { data, error } = await supabase
      .from('matches')
      .select(`
        *,
        participant1:contacts!matches_participant1_id_fkey (
          id,
          name,
          gender,
          birthdate
        ),
        participant2:contacts!matches_participant2_id_fkey (
          id,
          name,
          gender,
          birthdate
        )
      `)
      .eq('event_id', eventId)
      .order('created_at');
    
    if (error) throw error;
    return data;
  },

  async create(match: Tables['matches']['Insert']) {
    const { data, error } = await supabase
      .from('matches')
      .insert(match)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, match: Tables['matches']['Update']) {
    const { data, error } = await supabase
      .from('matches')
      .update(match)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('matches')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

export const userInterestsApi = {
  async getByEventId(eventId: string) {
    const { data, error } = await supabase
      .from('user_interests')
      .select(`
        *,
        from_user:contacts!user_interests_from_user_id_fkey (
          id, name, gender, birthdate
        ),
        to_user:contacts!user_interests_to_user_id_fkey (
          id, name, gender, birthdate
        )
      `)
      .eq('event_id', eventId);

    if (error) throw error;
    return data;
  },

  async getByDateRange(startDate: string, endDate: string) {
    const { data, error } = await supabase
      .from('user_interests')
      .select(`
        *,
        event:events!inner (
          id, date, name, city
        ),
        from_user:contacts!user_interests_from_user_id_fkey (
          id, name, gender, birthdate
        ),
        to_user:contacts!user_interests_to_user_id_fkey (
          id, name, gender, birthdate
        )
      `)
      .gte('created_at', startDate)
      .lte('created_at', endDate);

    if (error) throw error;
    return data;
  }
};

export const statisticsApi = {
  async getEventStatistics() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    if (profile?.role === 'pr') {
      const { data, error } = await supabase
        .rpc('get_pr_event_statistics', { pr_user_id: user.id });

      if (error) throw error;

      return (data || []).map((stat: any) => ({
        id: stat.event_id,
        event_id: stat.event_id,
        men_count: stat.men_count || 0,
        women_count: stat.women_count || 0,
        total_participants: (stat.men_count || 0) + (stat.women_count || 0),
        men_revenue: stat.men_revenue || 0,
        women_revenue: stat.women_revenue || 0,
        total_revenue: stat.total_revenue || 0,
        match_count: stat.match_count || 0,
        event: {
          id: stat.event_id,
          name: stat.event_name,
          date: stat.event_date,
          time: stat.event_time,
          location: stat.event_location,
          city: stat.event_city,
          age_range: stat.event_age_range
        }
      }));
    }

    const { data, error } = await supabase
      .from('event_statistics')
      .select(`
        *,
        event:events (
          id,
          name,
          date,
          time,
          location,
          city,
          age_range
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async getEventStatisticsByEventId(eventId: string) {
    console.log('Fetching statistics for event:', eventId);
    const { data, error } = await supabase
      .from('event_statistics')
      .select('*')
      .eq('event_id', eventId)
      .single();
    
    if (error) {
      console.error('Error fetching event statistics by ID:', error);
      throw error;
    }

    console.log('Event statistics data for ID:', data);
    return data;
  },

  async getTotalStatistics() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    if (profile?.role === 'pr') {
      const { data: prStats, error: prError } = await supabase
        .rpc('get_pr_event_statistics', { pr_user_id: user.id });

      if (prError) throw prError;

      const { count: contactsCount } = await supabase
        .from('contacts')
        .select('*', { count: 'exact', head: true })
        .eq('created_by', user.id);

      const { count: locationsCount } = await supabase
        .from('locations')
        .select('*', { count: 'exact', head: true });

      const totals = (prStats || []).reduce((acc: any, curr: any) => ({
        total_participants: (acc.total_participants || 0) + (curr.men_count || 0) + (curr.women_count || 0),
        men_count: (acc.men_count || 0) + (curr.men_count || 0),
        women_count: (acc.women_count || 0) + (curr.women_count || 0),
        match_count: (acc.match_count || 0) + (curr.match_count || 0),
        men_revenue: (acc.men_revenue || 0) + (curr.men_revenue || 0),
        women_revenue: (acc.women_revenue || 0) + (curr.women_revenue || 0),
        total_revenue: (acc.total_revenue || 0) + (curr.total_revenue || 0)
      }), {});

      return {
        ...totals,
        locations_count: locationsCount || 0,
        contacts_count: contactsCount || 0
      };
    }

    const [eventStats, { count: locationsCount }, { count: contactsCount }] = await Promise.all([
      supabase
        .from('event_statistics')
        .select(`
          total_participants,
          men_count,
          women_count,
          match_count,
          men_revenue,
          women_revenue,
          total_revenue
        `),
      supabase
        .from('locations')
        .select('*', { count: 'exact', head: true }),
      supabase
        .from('contacts')
        .select('*', { count: 'exact', head: true })
    ]);

    if (eventStats.error) throw eventStats.error;

    const totals = eventStats.data.reduce((acc, curr) => ({
      total_participants: (acc.total_participants || 0) + (curr.total_participants || 0),
      men_count: (acc.men_count || 0) + (curr.men_count || 0),
      women_count: (acc.women_count || 0) + (curr.women_count || 0),
      match_count: (acc.match_count || 0) + (curr.match_count || 0),
      men_revenue: (acc.men_revenue || 0) + (curr.men_revenue || 0),
      women_revenue: (acc.women_revenue || 0) + (curr.women_revenue || 0),
      total_revenue: (acc.total_revenue || 0) + (curr.total_revenue || 0)
    }), {});

    return {
      ...totals,
      locations_count: locationsCount || 0,
      contacts_count: contactsCount || 0
    };
  }
};

export const permissionsApi = {
  async getAll() {
    console.log('Fetching menu permissions...');
    const { data, error } = await supabase
      .from('menu_permissions')
      .select('*')
      .order('display_order');

    if (error) {
      console.error('Error fetching menu permissions:', error);
      throw error;
    }

    // Invalidate cache after fetching fresh data
    const cache = new Map();
    cache.clear();

    console.log('Menu permissions:', data);
    return data;
  },

  async update(menuItem: string, role: 'admin' | 'organizer' | 'pr', value: boolean) {
    const field = `${role}_access`;
    const { error } = await supabase
      .from('menu_permissions')
      .update({ [field]: value })
      .eq('menu_item', menuItem);

    if (error) throw error;
  }
};

export const advertisementsApi = {
  async getAll() {
    const { data, error } = await supabase
      .from('advertisements')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async uploadImage(file: File): Promise<string> {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('User not authenticated');

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}-${Date.now()}.${fileExt}`;
    const filePath = fileName;

    const { error: uploadError } = await supabase.storage
      .from('advertisements')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('advertisements')
      .getPublicUrl(filePath);

    return publicUrl;
  },

  async deleteImage(imageUrl: string) {
    try {
      const path = imageUrl.split('/advertisements/').pop();
      if (!path) return;

      const { error } = await supabase.storage
        .from('advertisements')
        .remove([path]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  },

  async create(advertisement: any) {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('User not authenticated');

    const adWithCreator = {
      ...advertisement,
      created_by: user.id,
      event_id: advertisement.event_id || null
    };

    const { data, error } = await supabase
      .from('advertisements')
      .insert([adWithCreator])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, advertisement: any) {
    const { data, error } = await supabase
      .from('advertisements')
      .update({
        ...advertisement,
        updated_at: new Date().toISOString(),
        event_id: advertisement.event_id || null
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string) {
    const { data: adData, error: fetchError } = await supabase
      .from('advertisements')
      .select('image_url')
      .eq('id', id)
      .single();

    if (!fetchError && adData?.image_url) {
      await this.deleteImage(adData.image_url);
    }

    const { error } = await supabase
      .from('advertisements')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

export const userApi = {
  async getPendingUsers() {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, name, email, city, region, phone, role, created_at, approval_status, is_suspended, birthdate')
      .eq('approval_status', 'pending')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getAllUsers() {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, name, email, city, region, phone, role, created_at, approval_status, is_suspended, birthdate')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async approveUser(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ approval_status: 'approved' })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async rejectUser(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ approval_status: 'rejected' })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async suspendUser(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ is_suspended: true })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async reactivateUser(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ is_suspended: false })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteUser(userId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Non autorizzato');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ userId }),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Errore nell\'eliminazione dell\'utente');
  },

  async createStaffUser(data: { email: string; name: string; role: string; city?: string; region?: string; phone?: string; birthdate?: string }) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Non autorizzato');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-staff`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(data),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Errore nella creazione dell\'utente');
    return result;
  }
};

export const transactionCategoriesApi = {
  async getAll() {
    const { data, error } = await supabase
      .from('transaction_categories')
      .select('*')
      .order('business_area')
      .order('type')
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getByBusinessArea(businessArea: string, type?: 'income' | 'expense') {
    let query = supabase
      .from('transaction_categories')
      .select('*')
      .eq('business_area', businessArea)
      .order('display_order', { ascending: true })
      .order('name');

    if (type) {
      query = query.eq('type', type);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(category: { business_area: string; type: string; name: string }) {
    const { data: existing } = await supabase
      .from('transaction_categories')
      .select('display_order')
      .eq('business_area', category.business_area)
      .eq('type', category.type)
      .order('display_order', { ascending: false })
      .limit(1);

    const maxOrder = existing && existing.length > 0 ? existing[0].display_order : 0;

    const { data, error } = await supabase
      .from('transaction_categories')
      .insert([{ ...category, is_default: false, display_order: maxOrder + 1 }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, name: string) {
    const { data, error } = await supabase
      .from('transaction_categories')
      .update({ name })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('transaction_categories')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async move(id: string, targetBusinessArea: string) {
    const { data, error } = await supabase
      .from('transaction_categories')
      .update({ business_area: targetBusinessArea })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateOrder(id: string, displayOrder: number) {
    const { data, error } = await supabase
      .from('transaction_categories')
      .update({ display_order: displayOrder })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};
